import { PopupApiDto, PopupEventApiType } from '@b2x/storefront-api-js-client/src/dto';
import Cookies from 'js-cookie';
import React from 'react';
import { useExitIntent } from 'use-exit-intent';

import { ApiRequestConfig } from './api/useApiRequest';
import { usePopupsApi } from './api/usePopupsApi';
import { useAppContext } from './AppContext';
import { PopupContentTypeV2 } from './contentTypes';
import { cookies } from './cookies';
import { ModalButtonProps } from './Modal';
import { PopupBuilder } from './popup/PopupBuilder';
import { usePopupEngineContext } from './popup/PopupEngineContext';
import { useModals } from './useModals';
import { useStable } from './util';

export const usePopups = () => {
  const { handlePopups: _handlePopups } = usePopupsApi();
  const { showModal } = useModals();
  const { routeInfo } = useAppContext();
  const { availableActions } = usePopupEngineContext();

  const handlePopups = React.useCallback(
    (event: PopupEventApiType, options?: ApiRequestConfig) => {
      return new Promise<PopupApiDto<PopupContentTypeV2> | undefined>((resolve) => {
        if (routeInfo?.pathname) {
          console.log('handlePopups', event, routeInfo.pathname);

          // TODO: Prendo dai cookie la lista di popup da escludere
          const popupsToExclude: Array<string> = [];
          const allCookies = Cookies.get();
          console.log('handlePopups', 'allCookies', allCookies);
          Object.entries(allCookies).forEach(([key, value]) => {
            if (key.startsWith(cookies.getPrefixedKey('popup-'))) {
              popupsToExclude.push(value);
            }
          });

          // Chiamo le API per sapere se c'è qualcosa da mostrare
          return _handlePopups(
            event,
            routeInfo.pathname,
            popupsToExclude,
            { populate: { content: true, offer: true } },
            options
          ).then((response) => {
            console.log('handlePopups', 'handlePopups', response.data);
            const popupToShow = response.data.at(0);
            if (popupToShow) {
              resolve(popupToShow);
            } else {
              resolve(undefined);
            }
          });
        }
      });

      // TODO: deve tornare la dispose che userà la useEffect come cleanup function
    },
    [_handlePopups, routeInfo?.pathname]
  );

  const { registerHandler, updateSettings } = useExitIntent();
  const updateSettingsStable = useStable(updateSettings);
  // Attualmente è possibile usare lo useStable perché non esistono metodi per aggiornare in tempo reale le availableActions
  // Se venissero creati dovremmo cambiarlo con un useRef che aggiorna il .current
  const availableActionsStable = useStable(availableActions);

  const showPopupModal = React.useCallback(
    (popup: PopupApiDto<PopupContentTypeV2>, callback?: () => void) => {
      const buttons: Array<ModalButtonProps> =
        popup.content?.body.buttons?.map((button) => ({
          availableActions: availableActionsStable,
          callbacks: button.callback?.map((cb) => ({
            action: cb.action,
            close: cb.close,
            couponCode: cb.applyCoupon ? popup.offer?.id : undefined,
            externalUrl: cb.externalUrl,
            redirect: cb.redirect?.at(0),
          })),
          label: button.label,
          variant: button.variant,
        })) || [];

      showModal({
        buttons,
        children: <PopupBuilder popup={popup} />,
        className: popup.classes,
        closable: popup.closable,
        id: popup.id,
        isMinimized: popup.startMinimized,
        minimizable: popup.minimizable,
        minimizedProps: popup.content?.body.minimized,
        onClose: (popup.delay && popup.delay > 0) || popup.showOnExit ? undefined : callback,
        onOpen: () => {
          console.log('handlePopups', `popup ${popup.id} aperto`);
          // Creo il cookie per non farlo vedere più, che deve durare quanto popup.maxAge
          if (popup.maxAge !== 0) {
            cookies.setString(`popup-${popup.id}`, popup.id, {
              expires: popup.maxAge ? new Date(Date.now() + popup.maxAge * 1000) : undefined,
            });
          }
        },
        size:
          popup.size === 'SMALL'
            ? 'default'
            : popup.size === 'MEDIUM'
            ? 'large'
            : popup.size === 'LARGE'
            ? 'extra-large'
            : undefined,
        title: popup.content?.body.title ?? popup.code ?? '',
      });
    },
    [showModal, availableActionsStable]
  );

  const showPopup = React.useCallback(
    (popup: PopupApiDto<PopupContentTypeV2>, callback?: () => void) => {
      if (((popup.delay && popup.delay > 0) || popup.showOnExit) && callback) {
        callback();
      }

      const timeoutId = window.setTimeout(() => {
        if (popup.showOnExit) {
          updateSettingsStable({
            // DOCS: https://use-exit-intent.daltonmenezes.com/docs/getting-started/overview
            desktop: {
              delayInSecondsToTrigger: 0, // Delay in secondi per inattività sul sito ( è necessario settare triggerOnIdle )
              mouseLeaveDelayInSeconds: 0, // Delay in secondi dopo il mouse out ( è necessario settare triggerOnMouseLeave )
              triggerOnIdle: !popup.showOnExit,
              triggerOnMouseLeave: popup.showOnExit,
            },
            mobile: {
              delayInSecondsToTrigger: 0, // Delay in secondi per inattività sul sito ( è necessario settare triggerOnIdle )
              triggerOnIdle: !popup.showOnExit,
            },
          });
          registerHandler({
            handler: () => showPopupModal(popup, callback),
            id: popup.id,
          });
        } else {
          showPopupModal(popup, callback);
        }
      }, (popup.delay ?? 0) * 1000);

      timeoutsIds.current.push(timeoutId);

      // Deadline in secondi che disabilita i popup attivi
      const deadline = undefined;

      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      if (deadline && deadline > 0) {
        window.setTimeout(() => {
          // Pulisco il timeout corrente
          clearTimeout(timeoutId);

          if (popup.showOnExit) {
            // Aggiorno i settaggi dell'exit intent e lo disabilito
            updateSettingsStable({
              // DOCS: https://use-exit-intent.daltonmenezes.com/docs/getting-started/overview
              desktop: {
                triggerOnIdle: false,
                triggerOnMouseLeave: false,
              },
              mobile: {
                triggerOnIdle: false,
              },
            });
          }

          // Aggiorno la lista dei timeout attivi
          timeoutsIds.current = timeoutsIds.current.filter((id) => id !== timeoutId);
        }, deadline * 1000);
      }
    },
    [registerHandler, showPopupModal, updateSettingsStable]
  );

  const timeoutsIds = React.useRef<Array<number>>([]);

  React.useEffect(() => {
    const clearAllTimeouts = () => {
      timeoutsIds.current.forEach((id) => clearTimeout(id));
      timeoutsIds.current = [];
    };

    return clearAllTimeouts;
  }, []);

  return { handlePopups, showPopup };
};
